import type { DeskDetail, DeskMoveConnectionChoice, DeskMovePlan, DeskMoveResult } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowRight, Bot, CheckCircle2, CircleAlert, Database, Plug, Zap } from 'lucide-react';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Field } from '@/components/field';
import { ErrorState, LoadingRows } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { api } from '@/lib/api';
import { useApiMutation, useWorkspace } from '@/lib/hooks';
import { intlLocale, t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

const choiceValue = (c: DeskMoveConnectionChoice) => (c.mode === 'existing' ? `existing:${c.connectionId}` : c.mode);
const parseChoice = (v: string): DeskMoveConnectionChoice =>
  v.startsWith('existing:') ? { mode: 'existing', connectionId: v.slice('existing:'.length) } : ({ mode: v } as DeskMoveConnectionChoice);

/**
 * Moves a desk with everything on it (Work Items, chats, activity, views, tables with their rows) into another
 * workspace of the account (docs/features/desks.md). The plan shows what happens first; nothing changes before "Move".
 */
export function MoveDeskDialog({
  desk,
  children,
  open: controlledOpen,
  onOpenChange,
}: {
  desk: DeskDetail;
  children?: ReactNode; open?: boolean; onOpenChange?: (open: boolean) => void;
}) {
  const [innerOpen, setInnerOpen] = useState(false);
  const open = controlledOpen ?? innerOpen;
  const setOpen = onOpenChange ?? setInnerOpen;
  const [target, setTarget] = useState('');
  const [name, setName] = useState('');
  const [choices, setChoices] = useState<Record<string, DeskMoveConnectionChoice>>({});
  const [keepTables, setKeepTables] = useState(false);
  const [result, setResult] = useState<DeskMoveResult | null>(null);

  const accountId = useWorkspace(desk.workspaceId).data?.accountId;
  const workspaces = useQuery({
    queryKey: qk.accountWorkspaces(accountId ?? ''),
    queryFn: () => api.accounts.workspaces(accountId!),
    enabled: open && !!accountId,
  });
  const others = (workspaces.data ?? []).filter((w) => w.id !== desk.workspaceId);

  const plan = useQuery({
    queryKey: qk.deskMovePlan(desk.id, target),
    queryFn: () => api.desks.movePlan(desk.id, target),
    enabled: open && !!target,
    staleTime: 0,
  });

  // Start from the plan's suggestions for each target.
  useEffect(() => {
    if (!plan.data) return;
    setName(plan.data.deskNameTaken ? `${plan.data.deskName} 2` : plan.data.deskName);
    setChoices(Object.fromEntries(plan.data.connections.map((c) => [c.id, c.suggested])));
  }, [plan.data]);

  const move = useApiMutation(() => api.desks.move(desk.id, { workspaceId: target, name: name.trim(), connections: choices, keepTables }), {
    errorTitle: t('The desk was not moved'),
    invalidate: [qk.desk(desk.id), ...[desk.workspaceId, target].flatMap((ws) => [qk.desks(ws), qk.overview(ws), qk.agents(ws), qk.connections(ws), qk.skills(ws)])],
    onSuccess: setResult,
  });

  // Fresh on every opening, from its own trigger or from outside.
  useEffect(() => {
    if (!open) return;
    setResult(null);
    setTarget('');
    setKeepTables(false);
    move.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (plan.data && !plan.data.blockers.length && name.trim()) move.mutate();
  };

  const p = plan.data;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        {result ? (
          <MoveDone desk={desk} plan={p} result={result} onClose={() => setOpen(false)} />
        ) : (
          <form onSubmit={onSubmit} className="grid gap-5">
            <DialogHeader>
              <DialogTitle>{t('Move “{name}” to another workspace', { name: desk.name })}</DialogTitle>
              <DialogDescription>
                {t(
                  'Everything on the desk comes along: Work Items, chats, activity, triggers, goals, playbooks, views and its tables with their rows. Webhook URLs keep working.',
                )}
              </DialogDescription>
            </DialogHeader>

            <Field label={t('Workspace')}>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger>
                  <SelectValue placeholder={workspaces.isPending ? t('Loading workspaces …') : t('Choose a workspace')} />
                </SelectTrigger>
                <SelectContent>
                  {others.map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {workspaces.data && !others.length && (
                <p className="text-xs text-muted-foreground">{t('Your account has no other workspace yet. Create one in the workspace menu first.')}</p>
              )}
            </Field>

            {target &&
              (plan.isPending ? (
                <LoadingRows rows={3} />
              ) : plan.isError ? (
                <ErrorState error={plan.error} onRetry={() => void plan.refetch()} />
              ) : p ? (
                <>
                  {p.blockers.length > 0 && (
                    <div className="grid gap-1.5 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-200">
                      <span className="flex items-center gap-2 font-medium">
                        <CircleAlert className="size-4 shrink-0" /> {t('The desk can’t move yet')}
                      </span>
                      <ul className="grid list-disc gap-1 pl-6 text-xs">
                        {p.blockers.map((b) => (
                          <li key={b}>{b}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <Field
                    label={t('Desk name')}
                    error={p.deskNameTaken && name.trim().toLowerCase() === p.deskName.toLowerCase() ? t('{workspace} already has a desk with this name.', { workspace: p.workspaceName }) : undefined}
                  >
                    <Input value={name} onChange={(e) => setName(e.target.value)} required />
                  </Field>

                  <div className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{t('Comes along:')}</span>{' '}
                    {t('{workItems} Work Items, {chats} chats, {views} views, {events} activity entries', {
                      workItems: p.counts.workItems.toLocaleString(intlLocale),
                      chats: p.counts.conversations.toLocaleString(intlLocale),
                      views: p.counts.views.toLocaleString(intlLocale),
                      events: p.counts.events.toLocaleString(intlLocale),
                    })}
                    {p.skills.length > 0 && (
                      <>
                        {' '}
                        ·{' '}
                        {t('skills {list}', {
                          list: p.skills
                            .map((s) => (s.mode === 'existing' ? t('{name} (uses {workspace}’s)', { name: s.name, workspace: p.workspaceName }) : s.name))
                            .join(', '),
                        })}
                      </>
                    )}
                  </div>

                  {p.agents.length > 0 && (
                    <div className="grid gap-2">
                      <h3 className="text-sm font-medium">{t('Agents')}</h3>
                      <ul className="grid gap-1.5">
                        {p.agents.map((a) => (
                          <li key={a.id} className="flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-sm">
                            <Bot className="size-4 text-muted-foreground" />
                            <span className="font-medium">{a.newName ?? a.name}</span>
                            {a.role === 'lead' && <Badge tone="accent">{t('lead')}</Badge>}
                            {!a.onDesk && <Badge tone="muted">{t('earlier on this desk')}</Badge>}
                            <span className="ml-auto text-xs text-muted-foreground">
                              {a.mode === 'move' ? t('moves along') : t('copied – other desks still use the original')}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {p.connections.length > 0 && (
                    <div className="grid gap-2">
                      <div>
                        <h3 className="text-sm font-medium">{t('Connections')}</h3>
                        <p className="text-xs text-muted-foreground">{t('A copy takes its keys and sign-in along; skipped connections stay behind.')}</p>
                      </div>
                      {p.connections.map((c) => (
                        <div key={c.id} className="grid gap-2 rounded-md border p-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <Plug className="size-4 text-muted-foreground" />
                            <span className="text-sm font-medium">{c.name}</span>
                            <Badge tone={c.permissions.includes('write') ? 'amber' : 'neutral'}>{c.permissions.includes('write') ? t('read + write') : t('read only')}</Badge>
                            {c.shared && <Badge tone="muted">{t('other desks use it too')}</Badge>}
                          </div>
                          <Select
                            value={choiceValue(choices[c.id] ?? c.suggested)}
                            onValueChange={(v) => setChoices((prev) => ({ ...prev, [c.id]: parseChoice(v) }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {!c.shared && <SelectItem value="move">{t('Move it along')}</SelectItem>}
                              <SelectItem value="copy">{t('Copy it, with keys and sign-in')}</SelectItem>
                              {c.candidates.map((x) => (
                                <SelectItem key={x.id} value={`existing:${x.id}`}>
                                  {t('Use “{name}” of {workspace}', { name: x.name, workspace: p.workspaceName })}
                                </SelectItem>
                              ))}
                              <SelectItem value="skip">{t('Leave it behind')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  )}

                  {p.tables.length > 0 && (
                    <div className="grid gap-2">
                      <h3 className="text-sm font-medium">{t('Tables')}</h3>
                      <ul className="grid gap-1 text-sm">
                        {p.tables.map((tbl) => (
                          <li key={tbl.name} className="flex items-center gap-2">
                            <Database className="size-4 text-muted-foreground" />
                            <span className="font-mono text-[13px]">{tbl.name}</span>
                            <span className="text-xs text-muted-foreground">{t('about {n} rows', { n: tbl.rowsAbout.toLocaleString(intlLocale) })}</span>
                          </li>
                        ))}
                      </ul>
                      <label className="flex items-start gap-3 rounded-md border p-3">
                        <Switch checked={keepTables} onCheckedChange={setKeepTables} className="mt-0.5" />
                        <span className="grid gap-0.5">
                          <span className="text-sm font-medium">{t('Keep the originals in this workspace')}</span>
                          <span className="text-xs text-muted-foreground">
                            {t('Off: the tables are copied with their rows and then dropped here. On: other desks here can keep reading them.')}
                          </span>
                        </span>
                      </label>
                    </div>
                  )}

                  {p.notes.length > 0 && (
                    <ul className="grid gap-1 rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
                      {p.notes.map((n) => (
                        <li key={n}>{n}</li>
                      ))}
                    </ul>
                  )}
                </>
              ) : null)}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                {t('Cancel')}
              </Button>
              <Button type="submit" loading={move.isPending} disabled={!p || p.blockers.length > 0 || !name.trim()}>
                {t('Move desk')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MoveDone({ desk, plan, result, onClose }: { desk: DeskDetail; plan: DeskMovePlan | undefined; result: DeskMoveResult; onClose: () => void }) {
  const navigate = useNavigate();
  const workspaceId = result.workspaceId;
  const openDesk = () => {
    onClose();
    void navigate({ to: '/w/$workspaceId/desks/$deskId', params: { workspaceId, deskId: result.deskId } });
  };
  return (
    <div className="grid gap-5">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <CheckCircle2 className="size-5 text-emerald-600" />{' '}
          {t('“{name}” is now in {workspace}', { name: desk.name, workspace: plan?.workspaceName ?? t('the other workspace') })}
        </DialogTitle>
        <DialogDescription>{result.todo.length ? t('A few things to look at:') : t('Everything came along.')}</DialogDescription>
      </DialogHeader>
      {result.todo.length > 0 && (
        <ul className="grid gap-2">
          {result.todo.map((todo, i) => (
            <li key={i} className="flex gap-3 rounded-md border p-3">
              {todo.kind === 'trigger' ? (
                <Zap className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              ) : todo.kind === 'connection' ? (
                <Plug className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              ) : todo.kind === 'agent' ? (
                <Bot className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              ) : (
                <CircleAlert className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              )}
              <div className="grid min-w-0 gap-0.5">
                <span className="text-sm font-medium">{todo.title}</span>
                <span className="text-xs text-muted-foreground">{todo.detail}</span>
                {todo.agentId && (
                  <Link
                    to="/w/$workspaceId/agents/$agentId"
                    params={{ workspaceId, agentId: todo.agentId }}
                    onClick={onClose}
                    className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                  >
                    {t('Open agent')} <ArrowRight className="size-3" />
                  </Link>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      <DialogFooter>
        <Button onClick={openDesk}>
          {t('Open desk')} <ArrowRight />
        </Button>
      </DialogFooter>
    </div>
  );
}
